import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { cva } from 'class-variance-authority';
/**
 * Button Component (Layer 1 Primitive)
 *
 * A versatile button component with multiple variants, sizes, and states.
 * Built on design tokens for consistent theming and accessibility.
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click me
 * </Button>
 * ```
 */
// Using CVA for variant management
const buttonVariants = cva(
// Base styles
'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-uw-interactive-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none hover:scale-[1.02] active:scale-[0.98]', {
    variants: {
        variant: {
            primary: 'bg-uw-interactive-primary text-uw-text-inverse hover:bg-uw-interactive-primary-hover active:bg-uw-interactive-primary-active shadow-sm hover:shadow-md',
            secondary: 'bg-uw-interactive-secondary text-uw-text-inverse hover:bg-uw-interactive-secondary-hover active:bg-uw-interactive-secondary-active shadow-sm hover:shadow-md',
            ghost: 'bg-transparent text-uw-text-primary hover:bg-uw-surface-elevated active:bg-uw-bg-tertiary',
            danger: 'bg-uw-error text-uw-text-inverse hover:opacity-90 active:opacity-80 shadow-sm hover:shadow-md',
        },
        size: {
            sm: 'px-3 py-1.5 text-sm gap-1.5',
            md: 'px-4 py-2.5 text-base gap-2',
            lg: 'px-6 py-3 text-lg gap-3',
        },
    },
    defaultVariants: {
        variant: 'primary',
        size: 'md',
    },
});
export const Button = React.forwardRef(({ children, variant = 'primary', size = 'md', loading = false, leftIcon, rightIcon, fullWidth = false, disabled, className = '', type = 'button', ...props }, ref) => {
    const isDisabled = disabled || loading;
    return (_jsxs("button", { ref: ref, type: type, disabled: isDisabled, className: `${buttonVariants({ variant, size })} ${fullWidth ? 'w-full' : ''} ${className}`, "aria-busy": loading, "aria-disabled": isDisabled, ...props, children: [loading && (_jsxs("svg", { className: "animate-spin h-4 w-4", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", "aria-hidden": "true", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] })), !loading && leftIcon && _jsx("span", { "aria-hidden": "true", children: leftIcon }), _jsx("span", { children: children }), !loading && rightIcon && _jsx("span", { "aria-hidden": "true", children: rightIcon })] }));
});
Button.displayName = 'Button';
//# sourceMappingURL=Button.js.map